import { queryOptions, useQuery } from "@tanstack/react-query";
import * as z from "zod";
import { api } from "../http";
import { paginatedListSchema } from "@/lib/utils/zod";

export const permissionCreateFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().min(1, "Description is required"),
  resource: z.string().min(1, "Resource is required"),
  action: z.string().min(1, "Action is required"),
});

export const permissionUpdateFormSchema = permissionCreateFormSchema.extend({
  id: z.number(),
});

export const permissionSchema = permissionUpdateFormSchema.extend({
  is_active: z.boolean().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  deleted_at: z.string().nullable().optional(),
});

export const permissionsListSchema = paginatedListSchema(permissionSchema);

export type PermissionCreateFormData = z.infer<
  typeof permissionCreateFormSchema
>;
export type PermissionUpdateFormData = z.infer<
  typeof permissionUpdateFormSchema
>;
export type Permission = z.infer<typeof permissionSchema>;
export type PermissionsList = z.infer<typeof permissionsListSchema>;

export const permissionResourcesQueryOptions = queryOptions({
  queryKey: ["permissions", "resources"],
  queryFn() {
    // FIXME: add backend query once it's ready on the backend
    return ["users", "roles", "groups", "entities", "policies", "departments"];
  },
  initialData: [
    "users",
    "roles",
    "groups",
    "entities",
    "policies",
    "departments",
  ],
});
export const permissionActionsQueryOptions = queryOptions({
  queryKey: ["permissions", "actions"],
  queryFn() {
    // FIXME: add backend query once it's ready on the backend
    return [
      "list",
      "create",
      "read",
      "update",
      "delete",
      "manage",
      "add",
      "remove",
    ];
  },
  initialData: [
    "list",
    "create",
    "read",
    "update",
    "delete",
    "manage",
    "add",
    "remove",
  ],
});

type SearchParams = {
  page: number;
  per_page?: number;
  search?: string;
  resource?: string;
  action?: string;
  is_active?: boolean;
};

export function permissionsListQueryOptions(params: SearchParams) {
  return queryOptions({
    queryKey: ["permissions", "list", params],
    queryFn() {
      const searchParams = new URLSearchParams({
        page: params.page.toString(),
        per_page: (params.per_page ?? 10).toString(),
      });

      if (params.search) searchParams.set("resource", params.search);
      if (params.resource) searchParams.set("resource", params.resource);
      if (params.action) searchParams.set("action", params.action);
      if (params.is_active !== undefined)
        searchParams.set("is_active", params.is_active.toString());

      return api.raw
        .get("api/v1/permissions/paginated", { searchParams })
        .json<PermissionsList>();
    },
  });
}

export function permissionQueryOptions(id: number) {
  return queryOptions({
    queryKey: ["permissions", "detail", id],
    queryFn: async () => {
      const response = await api.raw
        .get(`api/v1/permissions/${id}/`)
        .json<Permission>();
      return response;
    },
  });
}

type AllPermsSearchParams = {
  resource?: string;
  action?: string;
  is_active?: boolean;
};
export function allPermissionsQueryOptions(params?: AllPermsSearchParams) {
  return queryOptions({
    queryKey: ["permissions", "list", "all", params],
    queryFn() {
      const searchParams = new URLSearchParams();
      if (params?.resource) searchParams.set("resource", params.resource);
      if (params?.action) searchParams.set("action", params.action);
      if (params?.is_active !== undefined)
        searchParams.set("is_active", params.is_active.toString());

      return api.raw
        .get("api/v1/permissions/", { searchParams })
        .json<Array<Permission>>();
    },
  });
}

export function usePermissionsList(params: SearchParams) {
  return useQuery(permissionsListQueryOptions(params));
}

export function useAllPermissions(params?: AllPermsSearchParams) {
  return useQuery(allPermissionsQueryOptions(params));
}

export function usePermission(id: number) {
  return useQuery(permissionQueryOptions(id));
}
